import { useState, useEffect } from "react";
import { X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

const EXPIRE_DATE = new Date("2026-04-30T23:59:59Z");
const STORAGE_KEY = "workitect_beta_popup_dismissed";

export const BetaAnnouncementPopup = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (new Date() >= EXPIRE_DATE) return;
    if (localStorage.getItem(STORAGE_KEY)) return;

    // Only show for truly new users — if they have user_preferences, they're existing
    const checkIfNewUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        // Not logged in — don't show the beta popup
        return;
      }

      const { data: prefs } = await supabase
        .from('user_preferences')
        .select('id, onboarding_checklist')
        .eq('user_id', session.user.id)
        .maybeSingle();

      // Also check linked accounts
      const { data: isArch } = await supabase.rpc('is_architect', { _user_id: session.user.id });

      // If they have preferences or are architect, they're an existing user
      if (prefs || isArch) return;

      // Don't stack on top of the differentiator intro slideshow — that
      // fires on the same first sign-in. The intro sets `intro_seen_at`;
      // once it's set, we're safe to show the beta popup on the next visit.
      const c = (prefs as { onboarding_checklist?: { intro_seen_at?: string | null } } | null)?.onboarding_checklist;
      if (c && !c.intro_seen_at) return;

      setVisible(true);
    };

    checkIfNewUser();
  }, []);

  if (!visible) return null;

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, "1");
    setVisible(false);
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={dismiss}
    >
      <div
        className="relative max-w-lg w-full rounded-2xl bg-card shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={dismiss}
          className="absolute top-3 right-3 z-10 rounded-full bg-black/50 p-1.5 text-white hover:bg-black/70 transition-colors"
          aria-label="Dismiss"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="aspect-video w-full">
          <iframe
            src="https://www.youtube.com/embed/DBvOAWB8T60"
            title="Beta Presentation"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full h-full"
          />
        </div>

        <div className="px-6 py-5 text-center">
          <a
            href="https://www.youtube.com/watch?v=DBvOAWB8T60"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline font-semibold text-base md:text-lg"
          >
            Click to watch the beta presentation on March 5, 2026.
          </a>
        </div>
      </div>
    </div>
  );
};
